import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';
import { Wand2, Menu, User, LogOut, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { CreditBalanceChip } from '@/components/CreditBalanceChip';
import { useLanguage } from '@/contexts/LanguageContext';
import { DEMO_WEBSITE, demoAvatar } from '@/lib/demoAssets';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

export function Header() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { t, isRTL } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [homeProgress, setHomeProgress] = useState(0);

  const isHome = location.pathname === '/home';

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
      if (isHome) {
        const t = Math.min(1, Math.max(0, window.scrollY / window.innerHeight));
        setHomeProgress(t);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isHome]);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { href: '/gallery', label: t('header.nav.gallery') },
    { href: '/how-it-works', label: t('header.nav.howItWorks') },
    { href: '/pricing', label: t('header.nav.pricing') },
    { href: '/reviews', label: t('header.nav.reviews') },
  ];



  const RUBBY_LETTERS = ['R', 'U', 'B', 'B', 'Y'];
  const accountAvatar = demoAvatar('Account.png');

  return (
    <header
      style={isHome ? { transform: `translateY(${-100 * (1 - homeProgress)}%)` } : undefined}
      className={cn(
      "fixed top-0 inset-inline-start-0 z-50 w-full transition-transform duration-300 overflow-visible bg-transparent border-b border-transparent text-white font-extrabold [&_a]:text-white [&_button]:text-white [&_a]:font-extrabold [&_button]:font-extrabold",
      scrolled ? "h-[5.25rem] lg:h-[5.25rem]" : "h-24 lg:h-[6.75rem]"
    )}>
      <Link
        to="/"
        aria-label="Rubby"
        dir="ltr"
        className={cn(
          "absolute left-1/2 -translate-x-1/2 top-4 flex items-center gap-1 pointer-events-auto",
          isHome && "hidden"
        )}
        style={{
          WebkitTextStroke: '6px #000',
          paintOrder: 'stroke fill',
          textShadow: '4px 5px 0 #000',
        }}
      >
        {RUBBY_LETTERS.map((l, i) => (
          <img
            key={i}
            src={`${DEMO_WEBSITE}/${l}.avif`}
            alt={l}
            draggable={false}
            className="h-12 w-auto select-none"
          />
        ))}
      </Link>

      <div className={cn(
        "container flex items-center justify-between transition-[height] duration-300",
        scrolled ? "h-[5.25rem]" : "h-24 lg:h-[6.75rem]"
      )}>
        {/* Language — left in LTR, left (end) in RTL */}
        <div className="flex items-center gap-3 h-full order-1 rtl:order-2">
          <LanguageSwitcher />
        </div>

        {/* Account — right in LTR, right (start) in RTL */}
        <div className="flex items-center gap-2 order-2 rtl:order-1">
          {user && <CreditBalanceChip className="hidden md:inline-flex" />}


          {/* Auth: Sign In (ghost) or User Dropdown */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="hidden lg:flex flex-col items-center h-auto w-auto p-0 gap-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.55)]" aria-label="User menu">
                  <img 
                    src={accountAvatar}
                    alt="" 
                    className="h-[60px] w-[60px] object-contain"
                  />
                  <span className="text-[11px] leading-none mt-0.5 font-bold">{t('header.nav.account')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-56">
                <DropdownMenuItem disabled className="cursor-default">
                  <User className="me-2 h-4 w-4" />
                  {user.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/account" className="flex items-center">
                    <Settings className="me-2 h-4 w-4" />
                    {t('header.nav.account')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
                  <LogOut className="me-2 h-4 w-4" />
                  {t('header.nav.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth" className="hidden lg:flex flex-col items-center h-auto w-auto p-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.55)]">
              <img 
                src={accountAvatar}
                alt="" 
                className="h-[60px] w-[60px] object-contain"
              />
              <span className="text-[11px] leading-none mt-0.5 font-bold">{t('header.nav.account')}</span>
            </Link>
          )}


          {/* Mobile Menu Trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden min-h-[44px] min-w-[44px]" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={isRTL ? "left" : "right"} className="flex flex-col w-[280px] sm:w-[320px]">
              {/* Mobile Nav */}
              <nav className="flex flex-col gap-1 mt-8">
                {navItems.map((item) => (
                  <SheetClose asChild key={item.href}>
                    <Link
                      to={item.href}
                      className={cn(
                        "block px-4 py-3 min-h-[2.75rem] text-[15px] font-medium tracking-normal rounded-lg transition-all duration-200",
                        isActive(item.href)
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-primary hover:bg-accent"
                      )}
                    >
                      {item.label}
                    </Link>
                  </SheetClose>
                ))}
              </nav>

              {/* Magical accent */}
              <div className="px-4 py-3 text-center">
                <span className="text-2xl">✨</span>
                <p className="font-handwritten text-muted-foreground/60 text-sm mt-1">
                  Let's create something magical
                </p>
              </div>

              {/* Mobile Auth */}
              <div className="mt-6 pt-4 border-t space-y-2">
                {user ? (
                  <>
                    <div className="px-4 py-2 text-sm text-muted-foreground truncate">
                      {user.email}
                    </div>
                    <SheetClose asChild>
                      <Link
                        to="/account"
                        className="flex items-center px-4 py-3 text-sm font-medium rounded-lg text-muted-foreground hover:text-primary hover:bg-accent transition-all duration-200"
                      >
                        <Settings className="me-2 h-4 w-4" />
                        {t('header.nav.account')}
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={signOut}
                      >
                        <LogOut className="me-2 h-4 w-4" />
                        {t('header.nav.signOut')}
                      </Button>
                    </SheetClose>
                  </>
                ) : (
                  <SheetClose asChild>
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <Link to="/auth">{t('header.nav.signIn')}</Link>
                    </Button>
                  </SheetClose>
                )}
              </div>

              {/* Sticky CTA at bottom */}
              <div className="mt-auto pt-4">
                <SheetClose asChild>
                  <Button
                    asChild
                    className="w-full bg-primary text-primary-foreground font-semibold rounded-full hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                  >
                    <Link to="/toy?new=true" className="flex items-center justify-center gap-2">
                      <Wand2 className="w-4 h-4" />
                      {t('header.nav.startCreating')}
                    </Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
